import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { programOfferingInputSchema, type ProgramOfferingInput } from '@pirouette/shared';
import { useCreateProgram } from '@/data/programs';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/programs/new')({
  component: NewProgram,
});

const programTypes = [
  ['recurring_class', 'Recurring class'],
  ['camp', 'Camp'],
  ['intensive', 'Intensive'],
  ['workshop', 'Workshop'],
  ['private_lesson', 'Private lesson'],
  ['semi_private', 'Semi-private'],
  ['trio', 'Trio'],
  ['drop_in', 'Drop-in'],
  ['adult_membership', 'Adult membership'],
  ['mobile_preschool', 'Mobile preschool'],
  ['rental', 'Rental'],
  ['event_choreography', 'Event choreography'],
  ['performance_group', 'Performance group'],
  ['competition_team', 'Competition team'],
] as const;

const deliveryModes = [
  ['in_person', 'In person'],
  ['online', 'Online'],
  ['hybrid', 'Hybrid'],
  ['offsite', 'Offsite'],
] as const;

const ghostLinkClass =
  'inline-flex h-11 items-center justify-center gap-2 rounded-[var(--radius-md)] bg-transparent px-5 font-medium text-[var(--color-ink)] text-[var(--text-base)] transition-[background,color,box-shadow] duration-[var(--duration-fast)] ease-[var(--ease-out-quart)] hover:bg-[var(--color-surface-sunken)] focus-visible:outline-none';

function optionalNumber(value: unknown) {
  if (value === '' || value == null) return undefined;
  return Number(value);
}

function NewProgram() {
  const navigate = useNavigate();
  const createProgram = useCreateProgram();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ProgramOfferingInput>({
    resolver: zodResolver(programOfferingInputSchema),
    mode: 'onBlur',
    defaultValues: {
      program_type: 'recurring_class',
      delivery_mode: 'in_person',
      active: true,
      requires_trial: false,
      requires_waiver: true,
    },
  });

  async function onSubmit(values: ProgramOfferingInput) {
    try {
      const created = await createProgram.mutateAsync(values);
      toast({ title: 'Program created', description: created.name, variant: 'success' });
      await navigate({ to: '/admin/programs/$id', params: { id: created.id } });
    } catch (err) {
      toast({
        title: "Couldn't save the program",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <div className="max-w-3xl space-y-8">
      <header>
        <Link
          to="/admin/programs"
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Programs
        </Link>
        <h1 className="mt-2">New program</h1>
      </header>

      <form
        onSubmit={(e) => void handleSubmit(onSubmit)(e)}
        noValidate
        className="surface-elevated grid gap-5 p-6"
      >
        <div>
          <Label htmlFor="program-name">Name</Label>
          <Input id="program-name" aria-invalid={!!errors.name} {...register('name')} />
          {errors.name && (
            <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
              {errors.name.message}
            </p>
          )}
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <Label htmlFor="program-type">Program type</Label>
            <select
              id="program-type"
              className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
              {...register('program_type')}
            >
              {programTypes.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="program-delivery">Delivery</Label>
            <select
              id="program-delivery"
              className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
              {...register('delivery_mode')}
            >
              {deliveryModes.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <Label htmlFor="program-capacity">Default capacity</Label>
            <Input
              id="program-capacity"
              type="number"
              min="1"
              aria-invalid={!!errors.default_capacity}
              {...register('default_capacity', { setValueAs: optionalNumber })}
            />
            {errors.default_capacity && (
              <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
                {errors.default_capacity.message}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="program-price">Default price (cents)</Label>
            <Input
              id="program-price"
              type="number"
              min="0"
              aria-invalid={!!errors.default_price_cents}
              {...register('default_price_cents', { setValueAs: optionalNumber })}
            />
            {errors.default_price_cents && (
              <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
                {errors.default_price_cents.message}
              </p>
            )}
          </div>
        </div>

        <div>
          <Label htmlFor="program-description">Description</Label>
          <textarea
            id="program-description"
            rows={4}
            className="w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3 focus-visible:outline-none"
            {...register('description')}
          />
        </div>

        <div className="flex gap-3">
          <Button type="submit" disabled={isSubmitting} aria-busy={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Create program'}
          </Button>
          <Link to="/admin/programs" className={ghostLinkClass}>
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
